import React, { useState } from 'react';

/**
 * NewsletterWidget.jsx
 * Unified Newsletter & Mail Service Subscription Component.
 * Supports Mailchimp, Constant Contact, Custom Webhook, and Local Storage subscriber management.
 */
export const NewsletterWidget = ({
    config = {},
    onUpdateConfig = () => {},
    variant = 'card', // 'card' | 'banner' | 'compact' | 'inline'
    title,
    subtitle,
    buttonText,
    placeholderText,
    isEditing = false,
    style = {},
}) => {
    const mailMod = config?.modules?.mailService || {};
    const enabled = mailMod.enabled !== false;
    const provider = mailMod.provider || 'mailchimp';

    const defaultTitle = title || mailMod.formTitle || 'Subscribe to Our Newsletter';
    const defaultSubtitle = subtitle || mailMod.formSubtitle || 'Get the latest news, updates and exclusive offers delivered straight to your inbox.';
    const defaultBtnText = buttonText || mailMod.buttonText || 'Subscribe Now';
    const defaultPlaceholder = placeholderText || mailMod.placeholderText || 'Enter your email address...';

    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [gdprChecked, setGdprChecked] = useState(false);
    const [loading, setLoading] = useState(false);
    const [statusMsg, setStatusMsg] = useState(null); // { type: 'success' | 'error', text: string }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !email.includes('@')) {
            setStatusMsg({ type: 'error', text: '⚠️ Please enter a valid email address.' });
            return;
        }

        if (mailMod.requireGdprConsent && !gdprChecked) {
            setStatusMsg({ type: 'error', text: '⚠️ Please accept the privacy consent checkbox to continue.' });
            return;
        }

        setLoading(true);
        setStatusMsg(null);

        try {
            // New subscriber object
            const newSubscriber = {
                id: `sub_${Date.now()}`,
                email: email.trim(),
                name: name.trim() || undefined,
                provider,
                status: mailMod.doubleOptIn ? 'pending_opt_in' : 'subscribed',
                createdAt: new Date().toISOString()
            };

            // Simulate Provider Endpoint or Send to Webhook
            if (provider === 'webhook' && mailMod.webhookUrl) {
                try {
                    await fetch(mailMod.webhookUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(newSubscriber)
                    });
                } catch (err) {
                    console.warn('Webhook request warning:', err);
                }
            }

            // Save to local subscribers array for Omnivaso dashboard sync
            const currentSubs = Array.isArray(mailMod.subscribers) ? mailMod.subscribers : [];
            const exists = currentSubs.some(s => s.email.toLowerCase() === email.trim().toLowerCase());
            
            let updatedSubs = currentSubs;
            if (!exists) {
                updatedSubs = [newSubscriber, ...currentSubs];
            }

            const updatedConfig = {
                ...config,
                modules: {
                    ...(config.modules || {}),
                    mailService: {
                        ...mailMod,
                        subscribers: updatedSubs
                    }
                }
            };

            onUpdateConfig(updatedConfig);

            // Small latency buffer for realistic user feedback
            setTimeout(() => {
                setLoading(false);
                setStatusMsg({
                    type: 'success',
                    text: mailMod.successMessage || (mailMod.doubleOptIn
                        ? '🎉 Thank you! Check your inbox to confirm your subscription.'
                        : '🎉 Welcome aboard! You have successfully subscribed.')
                });
                setEmail('');
                setName('');
            }, 600);

        } catch (err) {
            setLoading(false);
            setStatusMsg({ type: 'error', text: '❌ Failed to subscribe. Please try again later.' });
        }
    };

    if (!enabled && !isEditing) return null;

    // --- COMPACT VARIANT (for Footer / Sidebars) ---
    if (variant === 'compact') {
        return (
            <div style={{ width: '100%', ...style }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '1rem', color: 'inherit', fontWeight: 'bold' }}>
                    {defaultTitle}
                </h4>
                {defaultSubtitle && (
                    <p style={{ margin: '0 0 12px 0', fontSize: '0.82rem', opacity: 0.85, lineHeight: '1.4' }}>
                        {defaultSubtitle}
                    </p>
                )}
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {mailMod.collectName && (
                        <input
                            type="text"
                            placeholder="Your name (optional)"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid rgba(255,255,255,0.2)',
                                background: 'rgba(0,0,0,0.2)',
                                color: 'inherit',
                                fontSize: '0.85rem',
                                boxSizing: 'border-box'
                            }}
                        />
                    )}
                    <div style={{ display: 'flex', gap: '6px' }}>
                        <input
                            type="email"
                            required
                            placeholder={defaultPlaceholder}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            style={{
                                flex: 1,
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid rgba(255,255,255,0.2)',
                                background: 'rgba(0,0,0,0.2)',
                                color: 'inherit',
                                fontSize: '0.85rem',
                                outline: 'none',
                                boxSizing: 'border-box'
                            }}
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                background: '#3b82f6',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '8px 14px',
                                fontWeight: 'bold',
                                fontSize: '0.85rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {loading ? '...' : defaultBtnText}
                        </button>
                    </div>

                    {mailMod.requireGdprConsent && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', opacity: 0.8, cursor: 'pointer', marginTop: '4px' }}>
                            <input
                                type="checkbox"
                                checked={gdprChecked}
                                onChange={(e) => setGdprChecked(e.target.checked)}
                            />
                            {mailMod.gdprText || 'I agree to receive email updates.'}
                        </label>
                    )}

                    {statusMsg && (
                        <div style={{
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                            marginTop: '6px',
                            color: statusMsg.type === 'success' ? '#4ade80' : '#f87171'
                        }}>
                            {statusMsg.text}
                        </div>
                    )}
                </form>
            </div>
        );
    }

    // --- BANNER VARIANT (Horizontal Section) ---
    if (variant === 'banner') {
        return (
            <div style={{
                background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)',
                color: '#ffffff',
                borderRadius: '16px',
                padding: '32px',
                boxShadow: '0 12px 30px rgba(49, 46, 129, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '24px',
                position: 'relative',
                overflow: 'hidden',
                ...style
            }}>
                <div style={{ flex: '1 1 320px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '1.4rem' }}>💌</span>
                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#a5b4fc' }}>
                            {provider === 'mailchimp' ? 'Mailchimp Newsletter' : provider === 'constant_contact' ? 'Constant Contact' : provider === 'brevo' ? 'Brevo Newsletter' : 'Exclusive Dispatch'}
                        </span>
                    </div>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '1.4rem', fontWeight: '800' }}>
                        {defaultTitle}
                    </h3>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#c7d2fe', lineHeight: '1.5' }}>
                        {defaultSubtitle}
                    </p>
                </div>

                <form onSubmit={handleSubmit} style={{ flex: '1 1 360px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {mailMod.collectName && (
                            <input
                                type="text"
                                placeholder="Your Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                style={{
                                    flex: '1 1 120px',
                                    padding: '12px 16px',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(255,255,255,0.25)',
                                    background: 'rgba(255,255,255,0.1)',
                                    color: '#ffffff',
                                    fontSize: '0.9rem',
                                    outline: 'none'
                                }}
                            />
                        )}
                        <input
                            type="email"
                            required
                            placeholder={defaultPlaceholder}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            style={{
                                flex: '2 1 200px',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                border: '1px solid rgba(255,255,255,0.25)',
                                background: 'rgba(255,255,255,0.1)',
                                color: '#ffffff',
                                fontSize: '0.9rem',
                                outline: 'none'
                            }}
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                background: '#6366f1',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '12px 24px',
                                fontWeight: 'bold',
                                fontSize: '0.9rem',
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(99,102,241,0.4)',
                                transition: 'all 0.2s ease',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {loading ? 'Subscribing...' : defaultBtnText}
                        </button>
                    </div>

                    {mailMod.requireGdprConsent && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: '#c7d2fe', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={gdprChecked}
                                onChange={(e) => setGdprChecked(e.target.checked)}
                            />
                            {mailMod.gdprText || 'I agree to receive email notifications.'}
                        </label>
                    )}

                    {statusMsg && (
                        <div style={{
                            padding: '8px 14px',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            fontWeight: 'bold',
                            background: statusMsg.type === 'success' ? 'rgba(74, 222, 128, 0.15)' : 'rgba(248, 113, 113, 0.15)',
                            color: statusMsg.type === 'success' ? '#4ade80' : '#f87171',
                            border: `1px solid ${statusMsg.type === 'success' ? 'rgba(74, 222, 128, 0.3)' : 'rgba(248, 113, 113, 0.3)'}`
                        }}>
                            {statusMsg.text}
                        </div>
                    )}
                </form>
            </div>
        );
    }

    // --- STANDARD CARD VARIANT (Default) ---
    return (
        <div style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            padding: '36px 30px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
            textAlign: 'center',
            maxWidth: '560px',
            margin: '0 auto',
            position: 'relative',
            ...style
        }}>
            <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: '#eff6ff',
                color: '#3b82f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.6rem',
                margin: '0 auto 16px auto',
                boxShadow: '0 4px 12px rgba(59,130,246,0.15)'
            }}>
                📮
            </div>

            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.35rem', color: '#0f172a', fontWeight: '800' }}>
                {defaultTitle}
            </h3>
            
            <p style={{ margin: '0 0 24px 0', fontSize: '0.9rem', color: '#64748b', lineHeight: '1.5' }}>
                {defaultSubtitle}
            </p>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {mailMod.collectName && (
                    <input
                        type="text"
                        placeholder="Full Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            fontSize: '0.9rem',
                            color: '#0f172a',
                            boxSizing: 'border-box'
                        }}
                    />
                )}

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <input
                        type="email"
                        required
                        placeholder={defaultPlaceholder}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={{
                            flex: '1 1 240px',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            fontSize: '0.9rem',
                            color: '#0f172a',
                            outline: 'none',
                            boxSizing: 'border-box'
                        }}
                    />

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            background: '#3b82f6',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '12px 24px',
                            fontWeight: 'bold',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            boxShadow: '0 4px 12px rgba(59,130,246,0.3)',
                            transition: 'all 0.2s ease',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {loading ? 'Subscribing...' : defaultBtnText}
                    </button>
                </div>

                {mailMod.requireGdprConsent && (
                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '0.8rem', color: '#64748b', cursor: 'pointer', marginTop: '4px' }}>
                        <input
                            type="checkbox"
                            checked={gdprChecked}
                            onChange={(e) => setGdprChecked(e.target.checked)}
                        />
                        {mailMod.gdprText || 'I agree to receive promotional updates.'}
                    </label>
                )}

                {statusMsg && (
                    <div style={{
                        padding: '10px 16px',
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        background: statusMsg.type === 'success' ? '#dcfce7' : '#fee2e2',
                        color: statusMsg.type === 'success' ? '#15803d' : '#b91c1c',
                        border: `1px solid ${statusMsg.type === 'success' ? '#86efac' : '#fca5a5'}`,
                        marginTop: '8px'
                    }}>
                        {statusMsg.text}
                    </div>
                )}
            </form>

            <div style={{ marginTop: '16px', fontSize: '0.75rem', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                🔒 <span>Zero spam. Unsubscribe anytime in one click.</span>
            </div>
        </div>
    );
};

export default NewsletterWidget;
